import type { FC } from 'react';
import React, { useCallback } from 'react';
import { Iconify, Modal, ModalInner } from '@pflegenavi/web-components';
import {
  Alert,
  Card,
  IconButton,
  Stack,
  styled,
  Typography,
} from '@mui/material';

import type {
  ResidentType,
  ResidentWithAmount,
} from './ReceiptBatchQuickResidentSelectionForm';
import { ReceiptBatchQuickResidentSelectionForm } from './ReceiptBatchQuickResidentSelectionForm';
import { useFormikContext } from 'formik';
import type { ReceiptBatchFormValues } from '../ReceiptBatchForm';
import { useTranslation } from 'react-i18next';
import type { Gender } from '@pflegenavi/shared/api';
import { redistributeFees } from '../utils/receiptFees';

interface ReceiptBatchQuickResidentSelectionModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  showAll: boolean;
  onChangeShowAll: (showAll: boolean) => void;
  residents: Array<{
    id: string;
    name: string;
    firstName: string;
    lastName: string;
    gender: Gender;
  }>;
}

export const ReceiptBatchQuickResidentSelectionModal: FC<
  ReceiptBatchQuickResidentSelectionModalProps
> = ({ open, setOpen, residents, showAll, onChangeShowAll }) => {
  const {
    setFieldValue,
    values: { receipts, cashTransactionGroup },
  } = useFormikContext<ReceiptBatchFormValues>();

  const onConfirm = useCallback(
    (residents: ResidentWithAmount[], date?: Date) => {
      const newAddedReceipts = residents.map((resident) => ({
        residentId: resident.id,
        individualDate: date,
        amount: resident.amount,
        notes: resident.note,
      }));

      let finalReceipts;

      // this is relevant when the user didn't add any receipts yet, we remove the first empty row
      if (
        receipts.length === 1 &&
        receipts[0].amount === undefined &&
        receipts[0].residentId === undefined
      ) {
        finalReceipts = newAddedReceipts;
      } else {
        finalReceipts = [...receipts, ...newAddedReceipts];
      }

      const finalReceiptsWithFees = redistributeFees(
        finalReceipts,
        cashTransactionGroup?.separateFeeAmount
          ? -cashTransactionGroup.separateFeeAmount
          : 0,
        cashTransactionGroup?.immediateFeeAmount
          ? -cashTransactionGroup.immediateFeeAmount
          : 0
      );

      setFieldValue('receipts', finalReceiptsWithFees);
    },
    [receipts, cashTransactionGroup, setFieldValue]
  );

  return (
    <ReceiptBatchQuickResidentSelectionModalView
      key={open ? 'open' : 'closed'}
      open={open}
      setOpen={setOpen}
      residents={residents}
      onConfirm={onConfirm}
      showAll={showAll}
      onChangeShowAll={onChangeShowAll}
    />
  );
};

interface ReceiptBatchQuickResidentSelectionModalViewProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  residents: ResidentType[];
  onConfirm: (residents: ResidentWithAmount[], date?: Date) => void;
  showAll: boolean;
  onChangeShowAll: (showAll: boolean) => void;
}

export const ReceiptBatchQuickResidentSelectionModalView: FC<
  ReceiptBatchQuickResidentSelectionModalViewProps
> = ({ open, setOpen, residents, onConfirm, showAll, onChangeShowAll }) => {
  const { t } = useTranslation();

  const handleClose = (_: any, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (reason !== 'backdropClick') {
      setOpen(false);
    }
  };
  return (
    <Modal open={open} handleClose={handleClose}>
      <ModalInner>
        <ModalCard>
          <Stack
            sx={{
              gap: 3,
              flex: 1,
            }}
          >
            <Stack
              direction="row"
              sx={{
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                }}
              >
                {t('receipts.batch.form.resident-quick-selection.title')}
              </Typography>
              <IconButton
                onClick={() => setOpen(false)}
                sx={{
                  padding: '16px',
                  marginRight: '-8px',
                }}
              >
                <Iconify
                  icon={'eva:close-fill'}
                  sx={{
                    position: 'absolute',
                  }}
                  width={20}
                />
              </IconButton>
            </Stack>
            <Alert severity="info">
              {t('receipts.batch.form.resident-quick-selection.info-alert')}
            </Alert>
            <ReceiptBatchQuickResidentSelectionForm
              setOpen={setOpen}
              residents={residents}
              onConfirm={onConfirm}
              showAll={showAll}
              onChangeShowAll={onChangeShowAll}
            />
          </Stack>
        </ModalCard>
      </ModalInner>
    </Modal>
  );
};

const ModalCard = styled(Card)(({ theme }) =>
  theme.unstable_sx({
    p: 3,
    minWidth: 600,
    width: 1200,
    maxWidth: '100%',
    userSelect: 'none',
  })
);
